// apps/web/src/pages/admin/today/TodayCapacityBar.tsx — Phase 5 plan 05-04 (D-02)
// + Quick 260803-ger.
//
// Renders the aggregate `{booked} / {capacity} Plätze · {holdback} zurückgehalten`
// from `Query.dateSummary(date)`. The bar is the single source of truth
// for the remaining capacity on the day; the per-row counts are derived
// from the same source plus the list of reservations.

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { gqlRequest } from "../../../lib/api";
import { DATE_SUMMARY_QUERY } from "./today-operations";

interface TodaySummary {
  booked: number;
  capacity: number;
  holdback: number;
  remaining: number;
}

export function TodayCapacityBar({ date }: { date: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["dateSummary", date],
    queryFn: async () => {
      const res = await gqlRequest<{ dateSummary: TodaySummary }>(
        DATE_SUMMARY_QUERY,
        { date },
      );
      return res.dateSummary;
    },
  });

  if (isLoading) {
    return (
      <div
        className="card p-4 mb-4"
        data-testid="today-capacity-bar"
        data-loading="true"
        data-date={date}
      >
        <span className="text-text/60">…</span>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div
        className="card p-4 mb-4"
        data-testid="today-capacity-bar"
        data-error="true"
        data-date={date}
      >
        <span className="text-text/60">—</span>
      </div>
    );
  }

  return (
    <div
      className="card p-4 mb-4"
      data-testid="today-capacity-bar"
      data-booked={data.booked}
      data-capacity={data.capacity}
      data-holdback={data.holdback}
      data-date={date}
    >
      <span className="font-heading text-2xl text-text">
        {data.booked} / {data.capacity}{" "}
        <span className="text-base text-text/60">
          {t("admin.today.capacityBar.booked")}
        </span>
      </span>
      <span className="ml-4 text-sm text-text/60">
        · {data.holdback} {t("admin.today.capacityBar.holdback")}
      </span>
    </div>
  );
}
